import React, { useState, useEffect } from 'react';
import {
  Box, Typography, List, ListItem, ListItemIcon, ListItemText, IconButton, Breadcrumbs, Paper,
  Menu, MenuItem, TextField, Button
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import AddIcon from '@mui/icons-material/Add';
import api from '../services/api';

const initialFS = {
  '/': [
    { name: 'Documents', type: 'folder' },
    { name: 'Images', type: 'folder' },
    { name: 'todo.txt', type: 'file' }
  ],
  '/Documents': [
    { name: 'Projet.docx', type: 'file' },
    { name: 'Notes.txt', type: 'file' }
  ],
  '/Images': [
    { name: 'photo.jpg', type: 'file' },
    { name: 'logo.png', type: 'file' }
  ]
};

function getFiles(fs, path) {
  return fs[path] || [];
}

export default function explorerApp() {
  const [fs, setFS] = useState({}); 
  const [currentPath, setCurrentPath] = useState('/');
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [contextFile, setContextFile] = useState(null);
  const [renameDialog, setRenameDialog] = useState({ open: false, file: null, value: '' });
  const [newDialog, setNewDialog] = useState({ open: false, type: 'file', value: '' });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // --- Création du répertoire personnel à l'ouverture ---
  useEffect(() => {
    // On récupère le user depuis le localStorage
    const userStr = window.localStorage.getItem('user');
    let uuid7 = null;
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        uuid7 = user.uuid7 || user.uuid || user.id || null;
      } catch (e) {}
    }
    if (uuid7) {
      api.post('/create-folder', { uuid7, path: '' })
        .then(() => {})
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    setError('');
    const token = window.localStorage.getItem('token');
    const userStr = window.localStorage.getItem('user');
    let uuid7 = null;
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        uuid7 = user.uuid7 || user.uuid || user.id || null;
      } catch (e) {}
    }
    if (!uuid7) {
      setError("Impossible de trouver l'identifiant utilisateur (uuid7)");
      setFiles([]);
      setLoading(false);
      return;
    }
    let relPath = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
    api.get(`/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
      .then(res => setFiles(res.data))
      .catch(e => setError("Erreur lors du chargement des fichiers."))
      .finally(() => setLoading(false));
  }, [currentPath]);

  const safeFsCurrentPath = Array.isArray(fs[currentPath]) ? fs[currentPath] : [];

  const goUp = () => {
    if (currentPath === '/') return;
    const parts = currentPath.split('/').filter(Boolean);
    parts.pop();
    setCurrentPath(parts.length === 0 ? '/' : '/' + parts.join('/'));
    setSelected([]);
  };

  const openFolder = (name) => {
    setCurrentPath(currentPath === '/' ? `/${name}` : `${currentPath}/${name}`);
    setSelected([]);
  };

  const handleSelect = (file, e) => {
    if (e.ctrlKey || e.metaKey) {
      setSelected(sel => sel.includes(file.name) ? sel.filter(n => n !== file.name) : [...sel, file.name]);
    } else if (e.shiftKey && files.length > 0) {
      const idx = files.findIndex(f => f.name === file.name);
      const lastIdx = files.findIndex(f => f.name === selected[selected.length - 1]);
      const [start, end] = [Math.min(idx, lastIdx), Math.max(idx, lastIdx)];
      setSelected(files.slice(start, end + 1).map(f => f.name));
    } else {
      setSelected([file.name]);
    }
  };

  const handleDelete = () => {
    setFS(fs => ({
      ...fs,
      [currentPath]: Array.isArray(fs[currentPath]) ? fs[currentPath].filter(f => !selected.includes(f.name)) : []
    }));
    setSelected([]);
    setMenuAnchor(null);
  };

  const handleRename = () => {
    setRenameDialog({ open: true, file: selected[0], value: selected[0] });
    setMenuAnchor(null);
  };

  const confirmRename = () => {
    setFS(fs => ({
      ...fs,
      [currentPath]: Array.isArray(fs[currentPath]) ? fs[currentPath].map(f => f.name === renameDialog.file ? { ...f, name: renameDialog.value } : f) : []
    }));
    setRenameDialog({ open: false, file: null, value: '' });
    setSelected([renameDialog.value]);
  };

  const handleNew = (type) => {
    setNewDialog({ open: true, type, value: '' });
    setMenuAnchor(null);
  };

  const confirmNew = () => {
    const userStr = window.localStorage.getItem('user');
    let uuid7 = null;
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        uuid7 = user.uuid7 || user.uuid || user.id || null;
      } catch (e) {}
    }
    if (!uuid7) {
      setError("Impossible de trouver l'identifiant utilisateur (uuid7)");
      setNewDialog({ open: false, type: 'file', value: '' });
      return;
    }
    // Calcul du chemin relatif dans l'espace utilisateur
    let relPath = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
    let fullPath = relPath ? relPath + '/' + newDialog.value : newDialog.value;
    if (newDialog.type === 'folder') {
      api.post('/create-folder', { uuid7, path: fullPath })
        .then(() => {
          setFiles(f => [...f, { name: newDialog.value, type: 'folder' }]);
        })
        .catch(() => setError("Erreur lors de la création du dossier."));
    } else {
      api.post('/create-file', { uuid7, path: fullPath })
        .then(() => {
          setFiles(f => [...f, { name: newDialog.value, type: 'file' }]);
        })
        .catch(() => setError("Erreur lors de la création du fichier."));
    }
    setNewDialog({ open: false, type: 'file', value: '' });
  };

  const filteredFiles = files.filter(f => f.name.toLowerCase().includes(search.toLowerCase()));

  const pathParts = currentPath.split('/').filter(Boolean);
  let breadcrumbPaths = ['/'];
  for (let i = 0; i < pathParts.length; i++) {
    breadcrumbPaths.push('/' + pathParts.slice(0, i + 1).join('/'));
  }

  return (
    <Paper elevation={4} sx={{ p: 3, width: '100%', height: 'calc(100vh - 64px)', boxSizing: 'border-box', margin: 0, borderRadius: 0, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" gutterBottom>Explorateur de fichiers</Typography>
      {error && <Typography color="error">{error}</Typography>}
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Button size="small" variant="outlined" onClick={goUp} disabled={currentPath === '/'} startIcon={<ArrowUpwardIcon />}>Remonter</Button>
        <TextField
          value={currentPath}
          size="small"
          label="Chemin"
          sx={{ flex: 1 }}
          InputProps={{ readOnly: true }}
        />
        <TextField
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          label="Recherche"
        />
        <IconButton onClick={() => handleNew('folder')} title="Nouveau dossier"><AddIcon /></IconButton>
        <IconButton onClick={() => handleNew('file')} title="Nouveau fichier"><InsertDriveFileIcon /></IconButton>
      </Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <span style={{ cursor: 'pointer', color: '#1976d2' }} onClick={() => setCurrentPath('/')}>Racine</span>
        {pathParts.map((part, idx) => (
          <span
            key={idx}
            style={{ cursor: 'pointer', color: '#1976d2' }}
            onClick={() => setCurrentPath(breadcrumbPaths[idx + 1])}
          >{part}</span>
        ))}
      </Breadcrumbs>
      <Box flex={1} minHeight={0} overflow="auto">
        {loading ? (
          <Typography>Chargement...</Typography>
        ) : (
          <List>
            {filteredFiles.map((file, idx) => (
              <ListItem
                key={idx}
                button
                selected={selected.includes(file.name)}
                onClick={e => handleSelect(file, e)}
                onDoubleClick={() => file.type === 'folder' ? openFolder(file.name) : null}
                secondaryAction={
                  <IconButton onClick={e => { setMenuAnchor(e.currentTarget); setContextFile(file); }}>
                    <MoreVertIcon />
                  </IconButton>
                }
              >
                <ListItemIcon>
                  {file.type === 'folder' ? <FolderIcon color="primary" /> : <InsertDriveFileIcon />}
                </ListItemIcon>
                <ListItemText primary={file.name} />
              </ListItem>
            ))}
            {filteredFiles.length === 0 && (<ListItem><ListItemText primary="Dossier vide" /></ListItem>)}
          </List>
        )}
      </Box>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={handleRename} disabled={selected.length !== 1}>
          <DriveFileRenameOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Renommer
        </MenuItem>
        <MenuItem onClick={handleDelete} disabled={selected.length === 0}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Supprimer
        </MenuItem>
      </Menu>
      <Menu
        open={renameDialog.open}
        anchorEl={null}
        onClose={() => setRenameDialog({ open: false, file: null, value: '' })}
        anchorReference="none"
        sx={{ '& .MuiMenu-paper': { padding: 2 } }}
      >
        <Box display="flex" alignItems="center" gap={2} p={2}>
          <TextField
            label="Nouveau nom"
            value={renameDialog.value}
            onChange={e => setRenameDialog(d => ({ ...d, value: e.target.value }))}
            size="small"
          />
          <Button variant="contained" onClick={confirmRename}>Valider</Button>
        </Box>
      </Menu>
      <Menu
        open={newDialog.open}
        anchorEl={null}
        onClose={() => setNewDialog({ open: false, type: 'file', value: '' })}
        anchorReference="none"
        sx={{ '& .MuiMenu-paper': { padding: 2 } }}
      >
        <Box display="flex" alignItems="center" gap={2} p={2}>
          <TextField
            label={newDialog.type === 'folder' ? 'Nom du dossier' : 'Nom du fichier'}
            value={newDialog.value}
            onChange={e => setNewDialog(d => ({ ...d, value: e.target.value }))}
            size="small"
          />
          <Button variant="contained" onClick={confirmNew}>Créer</Button>
        </Box>
      </Menu>
    </Paper>
  );
}
